import { useQuery } from "@tanstack/react-query";
import {
  Card, CardContent, Stack, Box, Typography, Avatar, Alert, Skeleton,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import ConstructionRoundedIcon from "@mui/icons-material/ConstructionRounded";
import { useTenant } from "../context/TenantContext";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";

type Level = "info" | "success" | "warn" | "error";

interface LogEvent {
  time?: string; ip?: string; actor?: string; action?: string; detail?: string; level?: Level;
}
interface LogsResponse {
  tenant: string; events: LogEvent[]; stale: boolean; error?: string;
}

const LEVEL_META: Record<Level, { color: string; Icon: any; light: string }> = {
  info:    { color: "info.main",    Icon: InfoRoundedIcon,         light: "info.light" },
  success: { color: "success.main", Icon: CheckCircleRoundedIcon,  light: "success.light" },
  warn:    { color: "warning.main", Icon: WarningAmberRoundedIcon, light: "warning.light" },
  error:   { color: "error.main",   Icon: ErrorRoundedIcon,        light: "error.light" },
};

export function Logs() {
  const { tenant } = useTenant();
  const api = useApi();
  const q = useQuery({
    queryKey: ["logs", tenant],
    queryFn: () => api.call<LogsResponse>("/api/logs"),
    enabled: !!tenant, staleTime: 30_000,
  });

  const events = q.data?.events ?? [];
  const notDeployed = q.data?.error === "audit_table_not_deployed";

  return (
    <>
      <PageHeader
        eyebrow="Observabilidade"
        title="Logs de acesso"
        subtitle={<>Eventos de <strong>{tenant ?? "?"}</strong> · retenção 90d em BQ, 1a em GCS</>}
      />

      {notDeployed && (
        <Alert severity="info" icon={<ConstructionRoundedIcon />} sx={{ borderRadius: 2 }}>
          <strong>Coleta de logs em construção.</strong> A tabela <code>platform_audit.access_events</code> ainda não foi criada. Esta página vai popular automaticamente quando ela existir — nenhum mock é exibido aqui pra não confundir com dado real.
        </Alert>
      )}
      {q.data?.stale && !notDeployed && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Logs em cache ou fora do ar ({q.data.error ?? "unknown"}).
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          {q.isLoading && Array.from({ length: 5 }).map((_, i) => (
            <Stack key={i} direction="row" spacing={2} sx={{ py: 1.5 }}>
              <Skeleton variant="circular" width={34} height={34} />
              <Box sx={{ flex: 1 }}><Skeleton width="60%" /><Skeleton width="40%" /></Box>
            </Stack>
          ))}
          {!q.isLoading && events.length === 0 && (
            <Box sx={{ textAlign: "center", py: 5, color: "text.secondary" }}>
              <Typography variant="body2">Sem eventos de acesso para exibir.</Typography>
            </Box>
          )}
          <Stack divider={<Box sx={{ height: 1, bgcolor: "divider", mx: -2 }} />}>
            {events.map((l, i) => {
              const M = LEVEL_META[l.level ?? "info"];
              return (
                <Stack key={i} direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5 }}>
                  <Avatar sx={{ bgcolor: M.light, color: M.color, width: 34, height: 34 }}>
                    <M.Icon fontSize="small" />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">
                      <Box component="strong" sx={{ mr: 0.5 }}>{l.actor}</Box>
                      {l.action}
                    </Typography>
                    {l.detail && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {l.detail}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                      {l.time}
                    </Typography>
                    {l.ip && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" }, fontFamily: "monospace" }}>
                        {l.ip}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
